import { useTranslation } from 'react-i18next';
import { decodeHtmlEntities } from '../utils/decodeHtmlEntities';

interface OrderItem {
  productId: number;
  quantity: number;
  name?: string;
  price?: number;
  image?: string;
}

interface ShippingInfo {
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  postcode: string;
  email: string;
  phone: string;
}

interface BillingInfo {
  firstName: string;
  lastName: string;
  company?: string;
  nif?: string;
  address: string;
  city: string;
  state: string;
  postcode: string;
}

interface OrderSummaryProps {
  orderId: number;
  username: string;
  items: OrderItem[];
  subtotal: number;
  shippingCost: number;
  total: number;
  shippingMethodTitle: string;
  shipping: ShippingInfo;
  billing?: BillingInfo;
  comments?: string;
  onLogout: () => void;
}

export default function OrderSummary({
  orderId,
  username,
  items,
  subtotal,
  shippingCost,
  total,
  shippingMethodTitle,
  shipping,
  billing,
  comments,
  onLogout,
}: OrderSummaryProps) {
  const { t } = useTranslation();

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="print:bg-white">
      <div className="max-w-3xl mx-auto print:py-0 print:px-0">

        {/* Capçalera: logo + usuari */}
        <div className="bg-black rounded-t-lg p-6 flex items-center justify-between print:rounded-none">
          <img src="/logo.png" alt="Logo" className="h-12 w-auto" />
          <div className="text-white text-sm text-right">
            <div className="opacity-70">{t('orderSummary.loggedAs')}</div>
            <div className="font-semibold text-base">{username}</div>
          </div>
        </div>

        {/* Cos del resum */}
        <div className="bg-white shadow-sm rounded-b-lg p-6 print:shadow-none print:rounded-none print:px-0">

          {/* Títol i ID de comanda */}
          <div className="border-b pb-4 mb-6">
            <h1 className="text-2xl font-bold text-gray-900">{t('orderSummary.title')}</h1>
            <p className="text-gray-500 mt-1">
              {t('orderSummary.orderId')}: <span className="font-semibold text-gray-800">#{orderId}</span>
            </p>
          </div>

          {/* Productes */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-3">{t('cart.summary')}</h2>
            <div className="space-y-3">
              {items.map(item => (
                <div key={item.productId} className="flex items-center justify-between border-b pb-3">
                  <div className="flex items-center gap-3">
                    {item.image && (
                      <img
                        src={item.image}
                        alt={item.name ? decodeHtmlEntities(item.name) : ''}
                        className="h-12 w-12 object-contain bg-white border border-gray-200 rounded print:h-10 print:w-10"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                    <div>
                      <div className="font-medium text-gray-900">
                        {item.name ? decodeHtmlEntities(item.name) : `${t('products.product')} #${item.productId}`}
                      </div>
                      <div className="text-sm text-gray-500">
                        {item.price?.toFixed(2) || '0.00'}€ × {item.quantity}
                      </div>
                    </div>
                  </div>
                  <div className="font-semibold text-gray-900">
                    {((item.price || 0) * item.quantity).toFixed(2)}€
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="mb-6 bg-gray-50 rounded-lg p-4 print:bg-white print:border print:border-gray-200">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">{t('cart.subtotal')}</span>
              <span className="font-medium">{subtotal.toFixed(2)}€</span>
            </div>
            <div className="flex justify-between text-sm mb-3">
              <span className="text-gray-600">
                {t('cart.shipping')}
                {shippingMethodTitle && (
                  <span className="text-gray-400 ml-1">({decodeHtmlEntities(shippingMethodTitle)})</span>
                )}
              </span>
              <span className="font-medium">{shippingCost.toFixed(2)}€</span>
            </div>
            <div className="flex justify-between text-base font-bold border-t pt-3">
              <span>{t('cart.total')}</span>
              <span>{total.toFixed(2)}€</span>
            </div>
          </div>

          {/* Adreces: enviament i facturació */}
          <div className={`mb-6 ${billing ? 'grid grid-cols-2 gap-6' : ''}`}>
            <div>
              <h2 className="text-lg font-semibold mb-3">{t('cart.shippingDetails')}</h2>
              <div className="text-sm text-gray-700 space-y-1">
                <div className="font-medium">{shipping.firstName} {shipping.lastName}</div>
                <div>{shipping.address}</div>
                <div>{shipping.postcode} {shipping.city}{shipping.state ? `, ${shipping.state}` : ''}</div>
                {shipping.email && <div>{shipping.email}</div>}
                {shipping.phone && <div>{shipping.phone}</div>}
              </div>
            </div>

            {billing && (
              <div>
                <h2 className="text-lg font-semibold mb-3">{t('cart.differentBillingAddress')}</h2>
                <div className="text-sm text-gray-700 space-y-1">
                  <div className="font-medium">{billing.firstName} {billing.lastName}</div>
                  {billing.company && <div>{billing.company}</div>}
                  {billing.nif && <div>{t('cart.billingNif')}: {billing.nif}</div>}
                  <div>{billing.address}</div>
                  <div>{billing.postcode} {billing.city}{billing.state ? `, ${billing.state}` : ''}</div>
                </div>
              </div>
            )}
          </div>

          {/* Comentaris */}
          {comments && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold mb-2">{t('cart.comments')}</h2>
              <p className="text-sm text-gray-700 bg-gray-50 rounded p-3 print:bg-white print:border print:border-gray-200">
                {comments}
              </p>
            </div>
          )}

          {/* Botons (ocults en impressió) */}
          <div className="flex gap-3 mt-8 print:hidden">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-5 py-2 bg-black text-white rounded-lg hover:bg-gray-900 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              {t('orderSummary.print')}
            </button>
            <button
              onClick={onLogout}
              className="flex items-center gap-2 px-5 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              {t('orderSummary.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
